/**
 * Manually change an invoice status — for mailed checks, cash-at-office
 * payments, or voiding invoices without going through Stripe.
 *
 * POST /api/invoices/{id}/manual-status
 *   { status, payment_method?, reference?, note? }
 */
import { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Landmark, Banknote, CircleDollarSign, XCircle, RotateCcw } from "lucide-react";

const METHODS = [
    { value: "check", label: "Check",              Icon: Landmark },
    { value: "cash",  label: "Cash",               Icon: Banknote },
    { value: "other", label: "Other (manual)",     Icon: CircleDollarSign },
];

export function InvoiceStatusDialog({ open, onOpenChange, invoice, onUpdated }) {
    const [status, setStatus]           = useState("paid");
    const [method, setMethod]           = useState("check");
    const [reference, setReference]     = useState("");
    const [note, setNote]               = useState("");
    const [paidDate, setPaidDate]       = useState("");
    const [busy, setBusy]               = useState(false);

    // Reset every time the dialog opens with a new invoice
    useEffect(() => {
        if (!open) return;
        setStatus(invoice?.status === "paid" ? "unpaid" : "paid");
        setMethod("check");
        setReference("");
        setNote("");
        setPaidDate(new Date().toISOString().slice(0, 10));
    }, [open, invoice?.id, invoice?.status]);

    if (!invoice) return null;

    const submit = async () => {
        setBusy(true);
        try {
            const payload = { status };
            if (status === "paid") {
                payload.payment_method = method;
                if (reference) payload.reference = reference;
                if (paidDate) payload.paid_date = paidDate;
            }
            if (note) payload.note = note;
            const { data } = await api.post(
                `/invoices/${invoice.id}/manual-status`, payload,
            );
            toast.success(
                status === "paid"  ? "Invoice marked paid" :
                status === "void"  ? "Invoice voided" :
                                     "Invoice reopened",
            );
            onUpdated?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update status");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="invoice-status-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Change invoice status</DialogTitle>
                    <DialogDescription className="pt-1 text-xs">
                        {invoice.invoice_number} · {fmtMoney(invoice.total)} · currently {invoice.status}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            New status
                        </Label>
                        <RadioGroup
                            value={status}
                            onValueChange={setStatus}
                            className="mt-2 grid grid-cols-3 gap-2"
                        >
                            <StatusChoice value="paid"   label="Paid"     Icon={CircleDollarSign} tone="emerald" />
                            <StatusChoice value="unpaid" label="Reopen"   Icon={RotateCcw}       tone="amber" />
                            <StatusChoice value="void"   label="Void"     Icon={XCircle}         tone="rose" />
                        </RadioGroup>
                    </div>

                    {status === "paid" && (
                        <>
                            <div>
                                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Payment method
                                </Label>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    {METHODS.map(({ value, label, Icon }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            data-testid={`inv-status-method-${value}`}
                                            onClick={() => setMethod(value)}
                                            className={
                                                "border rounded-lg p-2 flex flex-col items-center gap-1 text-xs " +
                                                (method === value
                                                    ? "border-primary bg-primary/5 text-foreground"
                                                    : "border-border text-muted-foreground hover:border-primary/50")
                                            }
                                        >
                                            <Icon className="w-4 h-4" />
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="ref" className="text-xs uppercase tracking-wide text-muted-foreground">
                                    {method === "check" ? "Check number" : "Reference (optional)"}
                                </Label>
                                <Input
                                    id="ref"
                                    data-testid="inv-status-reference"
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    placeholder={method === "check" ? "e.g. 1042" : "e.g. deposit slip #"}
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="paid_date" className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Payment date
                                </Label>
                                <Input
                                    id="paid_date"
                                    data-testid="inv-status-paid-date"
                                    type="date"
                                    value={paidDate}
                                    onChange={(e) => setPaidDate(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <Label htmlFor="note" className="text-xs uppercase tracking-wide text-muted-foreground">
                            Internal note (optional)
                        </Label>
                        <Textarea
                            id="note"
                            data-testid="inv-status-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="For office reference — not visible to customer"
                            rows={2}
                            className="mt-1"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        data-testid="inv-status-save-btn"
                    >
                        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save change
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StatusChoice({ value, label, Icon, tone }) {
    const tones = {
        emerald: "peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-50 peer-data-[state=checked]:text-emerald-700",
        amber:   "peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-50 peer-data-[state=checked]:text-amber-700",
        rose:    "peer-data-[state=checked]:border-rose-500 peer-data-[state=checked]:bg-rose-50 peer-data-[state=checked]:text-rose-700",
    };
    return (
        <div className="relative">
            <RadioGroupItem
                value={value}
                id={`inv-status-${value}`}
                data-testid={`inv-status-${value}`}
                className="peer sr-only"
            />
            <Label
                htmlFor={`inv-status-${value}`}
                className={
                    "cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-1 " +
                    "border-border text-muted-foreground text-xs " +
                    "hover:border-primary/50 " + (tones[tone] || "")
                }
            >
                <Icon className="w-4 h-4" />
                {label}
            </Label>
        </div>
    );
}
